import React, { useState, useEffect } from 'react';
import { Send, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const SolicitudBeca = () => {
  // Usar siempre window.location.origin para que funcione con cualquier dominio
  const API = `${window.location.origin}/api`;
  
  const [loading, setLoading] = useState(true);
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [semestreSeleccionado, setSemestreSeleccionado] = useState('');
  const [respuestas, setRespuestas] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [becaActiva, setBecaActiva] = useState(null);
  const [misSolicitudes, setMisSolicitudes] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Cargar formulario de becas
      const resFormulario = await fetch(`${API}/becas/formulario`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!resFormulario.ok) {
        toast.error('No hay formulario de becas disponible');
        setLoading(false);
        return;
      }

      const dataFormulario = await resFormulario.json();
      setFormulario(dataFormulario);

      // Cargar preguntas
      const resPreguntas = await fetch(`${API}/becas/formulario/preguntas`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resPreguntas.ok) {
        const dataPreguntas = await resPreguntas.json();
        setPreguntas(dataPreguntas);
      }

      // Cargar semestres disponibles
      const resSemestres = await fetch(`${API}/semestres`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resSemestres.ok) {
        const dataSemestres = await resSemestres.json();
        setSemestres(dataSemestres.filter(s => s.is_active));
      }

      // Cargar beca activa si existe
      const resBecaActiva = await fetch(`${API}/becas/mi-beca-activa`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resBecaActiva.ok) {
        const dataBecaActiva = await resBecaActiva.json();
        if (dataBecaActiva.tiene_beca_activa) {
          setBecaActiva(dataBecaActiva.beca);
        }
      }

      // Cargar mis solicitudes
      const resSolicitudes = await fetch(`${API}/becas/mis-solicitudes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resSolicitudes.ok) {
        const dataSolicitudes = await resSolicitudes.json();
        setMisSolicitudes(dataSolicitudes);
      }

    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas({
      ...respuestas,
      [preguntaId]: valor
    });
  };

  const enviarSolicitud = async () => {
    // Validar semestre
    if (!semestreSeleccionado) {
      toast.error('Por favor selecciona un semestre');
      return;
    }

    // Validar respuestas requeridas
    const preguntasRequeridas = preguntas.filter(p => p.requerida);
    const faltantes = preguntasRequeridas.filter(p => !respuestas[p.id] || respuestas[p.id].trim() === '');
    
    if (faltantes.length > 0) {
      toast.error('Por favor completa todas las preguntas obligatorias');
      return;
    }

    try {
      setEnviando(true);

      const response = await fetch(`${API}/becas/solicitudes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          semestre_id: semestreSeleccionado,
          respuestas: respuestas
        })
      });

      if (response.ok) {
        toast.success('¡Solicitud de beca enviada exitosamente!');
        setSemestreSeleccionado('');
        setRespuestas({});
        await cargarDatos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al enviar solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar solicitud');
    } finally {
      setEnviando(false);
    }
  };

  const renderPregunta = (pregunta) => {
    const valor = respuestas[pregunta.id] || '';

    switch (pregunta.tipo) {
      case 'texto_corto':
        return (
          <input
            type="text"
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Tu respuesta"
          />
        );

      case 'texto_largo':
        return (
          <textarea
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Tu respuesta"
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="correo@ejemplo.com"
          />
        );

      case 'opcion_multiple':
        return (
          <select
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Selecciona una opción</option>
            {pregunta.opciones && pregunta.opciones.map((opcion, idx) => (
              <option key={idx} value={opcion}>{opcion}</option>
            ))}
          </select>
        );

      case 'verdadero_falso':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={pregunta.id}
                value="Verdadero"
                checked={valor === 'Verdadero'}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="h-4 w-4 text-blue-600"
              />
              <span>Verdadero</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={pregunta.id}
                value="Falso"
                checked={valor === 'Falso'}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                className="h-4 w-4 text-blue-600"
              />
              <span>Falso</span>
            </label>
          </div>
        );

      case 'fecha':
        return (
          <input
            type="date"
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        );

      default:
        return (
          <input
            type="text"
            value={valor}
            onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        );
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente', icon: '⏳' },
      aprobada: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprobada', icon: '✅' },
      rechazada: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazada', icon: '❌' }
    };
    
    const badge = badges[estado] || badges.pendiente;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!formulario) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-600" />
          <h2 className="text-xl font-semibold text-yellow-900 mb-2">
            No hay formulario de becas disponible
          </h2>
          <p className="text-yellow-700">
            Por favor contacta con la administración para más información sobre becas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          🎓 Solicitud de Beca
        </h1>
        <p className="text-gray-600 mt-2">Completa el formulario para solicitar una beca académica</p>
      </div>

      {/* Beca Activa Alert */}
      {becaActiva && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900">¡Tienes una beca activa!</h3>
              <p className="text-sm text-green-700 mt-1">
                Beca para <strong>{becaActiva.semestre_nombre}</strong> válida hasta el{' '}
                {new Date(becaActiva.fecha_vencimiento).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mis Solicitudes Recientes */}
      {misSolicitudes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">📋 Mis Solicitudes</h2>
          <div className="space-y-3">
            {misSolicitudes.slice(0, 3).map(solicitud => (
              <div key={solicitud.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{solicitud.semestre_nombre}</h3>
                    <p className="text-sm text-gray-600">
                      Solicitada el {new Date(solicitud.fecha_solicitud).toLocaleDateString()}
                    </p>
                    {solicitud.comentarios_admin && (
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Comentarios:</strong> {solicitud.comentarios_admin}
                      </p>
                    )}
                  </div>
                  {getEstadoBadge(solicitud.estado)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formulario de Solicitud */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{formulario.titulo}</h2>
        <div
          className="text-gray-700 mb-6 prose max-w-none"
          dangerouslySetInnerHTML={{ __html: formulario.descripcion }}
        />

        <div className="space-y-6">
          {/* Selección de Semestre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Semestre para el cual solicitas la beca *
            </label>
            <select
              value={semestreSeleccionado}
              onChange={(e) => setSemestreSeleccionado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecciona un semestre</option>
              {semestres.map(semestre => (
                <option key={semestre.id} value={semestre.id}>
                  {semestre.name} - {semestre.year}
                </option>
              ))}
            </select>
          </div>

          {/* Preguntas */}
          {preguntas.sort((a, b) => a.orden - b.orden).map((pregunta, index) => (
            <div key={pregunta.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="mb-3">
                <div className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      <div 
                        className="inline prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1"
                        dangerouslySetInnerHTML={{ __html: pregunta.titulo }}
                      />
                      {pregunta.requerida && <span className="text-red-600 ml-1">*</span>}
                    </h3>
                    {pregunta.descripcion && (
                      <div
                        className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: pregunta.descripcion }}
                      />
                    )}
                  </div>
                </div>
              </div>
              {renderPregunta(pregunta)}
            </div>
          ))}

          {/* Botón Enviar */}
          <div className="pt-4">
            <button
              onClick={enviarSolicitud}
              disabled={enviando}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {enviando ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Enviar Solicitud de Beca
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolicitudBeca;
